import { AppIcon } from '@/components/ui/app-icon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useOrganization, useOrganizationList, useUser } from '@clerk/react';

import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { iconSpinnerThird } from '@/lib/icons';

type PendingAction = 'accept-leave' | 'accept-stay' | null;

export const InvitationPrompt = () => {
  const { user } = useUser();
  const { organization: activeOrganization } = useOrganization();
  const { userInvitations, userMemberships, setActive } = useOrganizationList({
    userInvitations: { infinite: true },
    userMemberships: { infinite: true },
  });

  const dismissedRef = useRef<Set<string>>(new Set());
  const [dismissedTick, setDismissedTick] = useState(0);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);

  const pendingInvitation = useMemo(
    () =>
      userInvitations?.data?.find(
        (invitation) =>
          invitation.status === 'pending' &&
          !dismissedRef.current.has(invitation.id)
      ),
    // dismissedTick drives recomputation when we mutate the ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userInvitations?.data, dismissedTick]
  );

  // Only prompt when the user is already in an active org - otherwise the
  // onboarding flow (or a first-time sign-in) handles acceptance.
  if (!pendingInvitation || !activeOrganization || !user) {
    return null;
  }

  const invitedOrgName =
    pendingInvitation.publicOrganizationData.name ?? 'the new workspace';
  const invitedOrgId = pendingInvitation.publicOrganizationData.id;
  const currentOrgName = activeOrganization.name;
  const currentOrgId = activeOrganization.id;

  const dismiss = () => {
    dismissedRef.current.add(pendingInvitation.id);
    setDismissedTick((t) => t + 1);
  };

  const runAccept = async (leaveCurrent: boolean) => {
    setPendingAction(leaveCurrent ? 'accept-leave' : 'accept-stay');
    try {
      await pendingInvitation.accept();

      if (leaveCurrent) {
        try {
          await user.leaveOrganization(currentOrgId);
        } catch (error) {
          console.error('Failed to leave current organization', error);
          toast.error(
            `Joined ${invitedOrgName}, but we couldn't leave ${currentOrgName}. You can leave it from workspace settings.`
          );
        }
      }

      if (setActive) {
        await setActive({ organization: invitedOrgId });
      }

      await Promise.all([
        userInvitations?.revalidate?.(),
        userMemberships?.revalidate?.(),
      ]);

      toast.success(
        leaveCurrent
          ? `Joined ${invitedOrgName} and left ${currentOrgName}.`
          : `Joined ${invitedOrgName}.`
      );
    } catch (error) {
      console.error('Failed to accept organization invitation', error);
      toast.error(
        `Unable to accept the invitation to ${invitedOrgName}. Please try again.`
      );
    } finally {
      setPendingAction(null);
    }
  };

  const isBusy = pendingAction !== null;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isBusy) dismiss();
      }}
    >
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Join {invitedOrgName}?</DialogTitle>
          <DialogDescription>
            You've been invited to <strong>{invitedOrgName}</strong>. You're
            currently a member of <strong>{currentOrgName}</strong>. You can
            keep both and switch between them any time.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='flex-col gap-2 sm:flex-col sm:space-x-0'>
          {/* Keeping both is the default, so it leads. */}
          <Button
            variant='default'
            onClick={() => runAccept(false)}
            disabled={isBusy}
            className='w-full'
          >
            {pendingAction === 'accept-stay' ? (
              <>
                <AppIcon
                  icon={iconSpinnerThird}
                  className='mr-2 animate-spin'
                  style={{ fontSize: 16 }}
                />
                Joining…
              </>
            ) : (
              'Accept and stay in both'
            )}
          </Button>
          <Button
            variant='outline'
            onClick={() => runAccept(true)}
            disabled={isBusy}
            className='w-full'
          >
            {pendingAction === 'accept-leave' ? (
              <>
                <AppIcon
                  icon={iconSpinnerThird}
                  className='mr-2 animate-spin'
                  style={{ fontSize: 16 }}
                />
                Joining…
              </>
            ) : (
              `Accept and leave ${currentOrgName}`
            )}
          </Button>
          <Button
            variant='ghost'
            onClick={dismiss}
            disabled={isBusy}
            className='w-full'
          >
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
